// Fixed DatePicker component with zoom-responsive layout
import { forwardRef, useState } from "react";
import { format } from "date-fns";
import { Popover, PopoverTrigger, PopoverContent } from "../../Components/UI/popover";
import { Button } from "../../Components/UI/button";
import { Calendar } from "../../Components/UI/calendar";
import { Label } from "../../Components/UI/label";
import { CalendarIcon } from "lucide-react";
import { cn } from "../../Lib/Cn";

export const DatePicker = forwardRef(({
    value,
    onChange,
    placeholder = "Select",
    label,
    required,
    fieldID,
    className,
    isDisabled = false,
    disabled = false,
    disableButton = false,
    param = "",
    defaultMonth,
    onKeyDown,
    minDate,
    maxDate,
    fromYear = 1900,
    toYear = 2050,
    additionalCss = "",
    disableMonthNavigation = false,
    useAsFilter = false,
    popoverClassName = ""
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const currentYear = new Date().getFullYear();
    const dynamicFromYear = currentYear - 5;
    const dynamicToYear = currentYear + 5;
    // Fix: Only treat boolean disabled values as component-level disabled
    const isActuallyDisabled = isDisabled || (typeof disabled === 'boolean' && disabled);
    const isButtonDisabled = isActuallyDisabled || disableButton;

    return (
        <div className={cn("space-y-1.5", className)}>
            {label && (
                <Label htmlFor={fieldID} required={required} className={className}>
                    {label}
                </Label>
            )}

            <Popover
                open={isOpen && !isButtonDisabled}
                onOpenChange={(open) => {
                    if (!isButtonDisabled) {
                        setIsOpen(open);
                    }
                }}
            >
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        id={fieldID}
                        data-field-id={fieldID}
                        type="button"
                        tabIndex={isButtonDisabled ? -1 : 0}
                        disabled={isButtonDisabled}
                        className={cn(
                            "w-full flex justify-between items-center font-normal text-sm text-left overflow-hidden p-0 sahi-input border-sahi-grey !gap-0",
                            useAsFilter ? "h-10 border border-transparent hover:border-sahi-primary-color" : "h-11 border-input", "bg-white shadow-xs transition-[color,box-shadow] outline-none",
                            !isButtonDisabled && "hover:border-gray-400 cursor-pointer",
                            !isButtonDisabled && "focus:border-sahi-primary-color focus:ring-1 focus:ring-sahi-primary-color",
                            isButtonDisabled && "cursor-not-allowed opacity-50 bg-gray-50",
                            additionalCss
                        )}

                        onClick={() => {
                            if (!isButtonDisabled) {
                                setIsOpen(true);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (!isButtonDisabled && onKeyDown) {
                                onKeyDown(e);
                            }
                            if (!isButtonDisabled && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                setIsOpen(true);
                            }
                        }}
                    >
                        {/* Text area with responsive padding and proper flex behavior */}
                        <span className={cn(
                            "flex-1 min-w-0 px-2 sm:px-3", // Responsive padding, min-w-0 allows shrinking
                            value ? "text-gray-900" : "text-muted-foreground"
                        )}>
                            <span className="block truncate"> {/* Ensures text truncates instead of overflowing */}
                                {value ? (() => {
                                    try {
                                        const date = new Date(value);
                                        if (isNaN(date.getTime())) return placeholder;
                                        return format(date, "dd-MMM-yy");
                                    } catch (error) {
                                        return placeholder;
                                    }
                                })() : placeholder}
                            </span>
                        </span>

                        {/* Icon area with responsive sizing and flexible width */}
                        <span className={cn(
                            "flex-shrink-0 h-full inline-flex items-center justify-center",
                            "px-2 sm:px-3 bg-white border-l border-gray-300", // Responsive padding
                            "min-w-[1.5rem] sm:min-w-[1.5rem]", // Responsive minimum width
                            isButtonDisabled && "opacity-50"
                        )}>
                            <CalendarIcon className="h-4 w-4 text-gray-700 flex-shrink-0" />
                        </span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className={cn("w-auto p-0 bg-white border border-gray-200 shadow-lg z-[10030]", popoverClassName)}
                    align="start"
                    sideOffset={5}
                >
                    <Calendar
                        mode="single"
                        selected={value}
                        captionLayout={disableMonthNavigation ? "label" : "dropdown"}
                        fromYear={dynamicFromYear}
                        toYear={dynamicToYear}
                        className="rdp-dropdown-custom"
                        fixedWeeks={true}
                        onSelect={(date) => {
                            if (date) {
                                onChange(date);
                                setIsOpen(false);
                            }
                        }}
                        disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            if (param === "noFuture" && date > today) return true;
                            if (minDate && date < minDate) return true;
                            if (maxDate && date > maxDate) return true;

                            return false;
                        }}
                        defaultMonth={defaultMonth}
                        disableMonthNavigation={disableMonthNavigation}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
});